import * as React from 'react';
import { inject, observer } from 'mobx-react';

import { AnalyticsStore, ContextStore } from 'bernie-core';
import { Viewport, ViewLarge } from 'bernie-view';

import { liveAnnounce } from 'uitk-react-utils';

import { MesoInlineBannerComponent, shouldMesoInlineBannerRender } from 'components/meso';
import { EbfOffer, Offer, SuggestedFilterOffer } from 'components/search/listings/offer';
import { getListingsCustomMetrics, openOffer } from 'utility';

import {
  AdvertisingStore,
  ClientMetadataStore,
  DialogsStore,
  ListingsStore,
  OpinionLabStore,
  FlightsSelectedJourneyReviewStore
} from 'stores';
import { FlightsListing } from 'types/search/listings/offer';
import { Dialogs } from 'components/search/dialogs';
import { DetailsAndFaresSheet } from 'components/shared/details-and-fares';
import { focusElement, trackAnalytics } from 'utility';
import { ActionType, AnalyticsType } from 'types/shared';
import analyticsWrapper from 'utility/analytics-wrapper';
import { OpinionLabListing } from 'components/shared/opinion-lab';
import { trackAnalyticsList } from 'utility';
import { FlightsActionType, FlightAction, FlightsStandardOffer, FlightsBargainFareOffer } from '__generated__/api';

export interface ListingsProps {
  context?: ContextStore;
  listingsStore?: ListingsStore;
  analytics?: AnalyticsStore;
  dialogsStore?: DialogsStore;
  opinionLabStore?: OpinionLabStore;
  clientMetadataStore?: ClientMetadataStore;
  advertisingStore?: AdvertisingStore;
  flightsSelectedJourneyReviewStore?: FlightsSelectedJourneyReviewStore;
}

export interface ListingsState {
  isForcedChoiceModalVisible?: boolean;
  maxOfferViewed: number;
  availableFaresInformation?:
    | FlightsStandardOffer.FlightsJourneyAvailableFaresInformation
    | FlightsBargainFareOffer.FlightsJourneyAvailableFaresInformation;
  isEbfOffer?: boolean;
}

const renderOffer = (
  offer: FlightsListing,
  offerIndex: number,
  selectedOfferRef: React.Ref<HTMLButtonElement>,
  updateForcedChoiceModal: (fisUrl?: string, packageableUrl?: string) => void,
  openForcedChoiceModal: boolean,
  updateOnViewedAnalytics: (anaytics: AnalyticsType, offerIndex: number) => void,
  lastOfferViewAnalytics?: AnalyticsType,
  analytics?: AnalyticsStore,
  openDetails?: (
    offerIndex: number,
    availableFaresInformation:
      | FlightsStandardOffer.FlightsJourneyAvailableFaresInformation
      | FlightsBargainFareOffer.FlightsJourneyAvailableFaresInformation
  ) => void,
  selectedOfferIndex?: number,
  handleOfferSelect?: (selectOfferAction: FlightAction.Fragment) => void,
  handleEbfOfferSelect?: (selectOfferAction?: ActionType, isSmallViewport?: boolean) => void
) => {
  switch (offer.__typename) {
    case 'FlightsSuggestedFilterMessageListing':
      return (
        <li key={`offer-${offerIndex}`}>
          <SuggestedFilterOffer offer={offer} />
        </li>
      );
    case 'FlightsStandardOffer':
      return (
        <li key={`offer-${offerIndex}`} data-test-id="offer-listing">
          <Offer
            offer={offer}
            offerIndex={offerIndex}
            selectedOfferRef={selectedOfferRef}
            handleForcedChoiceModal={updateForcedChoiceModal}
            openForcedChoiceModal={openForcedChoiceModal}
            lastOfferViewAnalytics={lastOfferViewAnalytics}
            updateOnViewedAnalytics={updateOnViewedAnalytics}
            analytics={analytics}
            openDetails={openDetails}
            selectedOfferIndex={selectedOfferIndex}
            handleOfferSelect={handleOfferSelect}
          />
        </li>
      );
    case 'FlightsBargainFareOffer':
      return (
        <li key={`offer-${offerIndex}`} data-test-id="offer-listing" className="ebf-module">
          <EbfOffer
            offer={offer}
            openDetails={openDetails}
            offerIndex={offerIndex}
            selectedOfferRef={selectedOfferRef}
            selectedOfferIndex={selectedOfferIndex}
            handleOfferSelect={handleEbfOfferSelect}
            openForcedChoiceModal={openForcedChoiceModal}
          />
        </li>
      );
    case 'FlightsFeedback':
      if (offer.feedbackLinkAction.relativeURI !== null) {
        return (
          <li key={`offer-${offerIndex}`}>
            <OpinionLabListing flightsFeedback={offer} />
          </li>
        );
      }
  }
};

const renderInlineIntentMedia = (offerIndex: number) => {
  if (offerIndex === 13) {
    return (
      <li data-test-id="meso-inline-intent-media-ad">
        <meso-intent-media-search-compare slot="IntentMediaResultAd1" class="meso-inline-intent-media-ads" />
      </li>
    );
  }

  return null;
};

const isKeyboardEvent = event => event && event.detail === 0;

@inject(
  'listingsStore',
  'analytics',
  'context',
  'dialogsStore',
  'opinionLabStore',
  'clientMetadataStore',
  'advertisingStore',
  'flightsSelectedJourneyReviewStore'
)
@observer
export class Listings extends React.Component<ListingsProps, ListingsState> {
  private readonly selectedOfferRef;
  public constructor(props: ListingsProps) {
    super(props);
    this.selectedOfferRef = React.createRef();
    this.state = {
      isForcedChoiceModalVisible: false,
      maxOfferViewed: -1
    };
  }

  setupBeforeUnloadListener = () => {
    window.addEventListener('beforeunload', () => {
      const { listingsStore, analytics } = this.props;
      listingsStore.trackOnViewedAnalytics(analytics);
    });
  };

  removeBeforeUnloadListener = () => {
    window.removeEventListener('beforeunload', () => {
      const { listingsStore, analytics } = this.props;
      listingsStore.trackOnViewedAnalytics(analytics);
    });
  };

  public componentDidMount() {
    this.setupBeforeUnloadListener();
    const { analytics, listingsStore, clientMetadataStore, advertisingStore } = this.props;
    const accessibilityMessage = `${listingsStore.appliedSortingAccessibility} ${
      listingsStore.appliedFilterAccessibility ? listingsStore.appliedFilterAccessibility : ''
    }`;

    const options = getListingsCustomMetrics(clientMetadataStore);

    analyticsWrapper.markPageUsable(options, analytics);
    liveAnnounce(accessibilityMessage);
    const pinnedListingAnalytics =
      listingsStore.flightsListingsAnalytics && listingsStore.flightsListingsAnalytics.pinnedListingDisplayedAnalytics;
    trackAnalytics(pinnedListingAnalytics, analytics);
    advertisingStore.showCLAds(true);
  }

  public componentWillUnmount() {
    this.removeBeforeUnloadListener();
    const { listingsStore, analytics } = this.props;
    listingsStore.trackOnViewedAnalytics(analytics);
    listingsStore.setMaxViewOfferAnalytics(null);
  }

  public updateOnViewedAnalytics = (offerViewAnalytics: AnalyticsType, offerIndex: number) => {
    const { listingsStore } = this.props;
    const { maxOfferViewed } = this.state;

    if (offerIndex > maxOfferViewed) {
      listingsStore.setMaxViewOfferAnalytics(offerViewAnalytics);
      this.setState({
        maxOfferViewed: offerIndex
      });
    }
  };

  private toggleForcedChoiceModalState = () => {
    this.setState(prevState => ({
      isForcedChoiceModalVisible: !prevState.isForcedChoiceModalVisible
    }));
  };

  public handleForcedChoiceModalAction = (actionType: FlightsActionType) => {
    const { dialogsStore } = this.props;
    actionType === FlightsActionType.GO_TO_PACKAGES
      ? dialogsStore.resetForcedChoiceDisplayedCounter()
      : dialogsStore.incrementForcedChoiceDisplayedCounter();
    this.toggleForcedChoiceModalState();
  };

  public updateForcedChoiceModal = (fisUrl?: string, packageableUrl?: string) => {
    const { dialogsStore } = this.props;
    const forcedChoiceModal = dialogsStore && dialogsStore.getForcedChoiceModalDialog();
    if (forcedChoiceModal) {
      forcedChoiceModal.actions.map((action: ActionType) => {
        switch (action.type) {
          case FlightsActionType.GO_TO_PACKAGES:
            action.relativePackageableURI = { relativePath: packageableUrl, value: null };
          case FlightsActionType.GO_TO_DETAILS:
            action.relativeURI = { relativePath: fisUrl, value: null };
          default:
        }
      });

      this.toggleForcedChoiceModalState();
    }
  };

  private setDetailsOpenState = (
    selectedOfferIndex: number,
    availableFaresInformation:
      | FlightsStandardOffer.FlightsJourneyAvailableFaresInformation
      | FlightsBargainFareOffer.FlightsJourneyAvailableFaresInformation,
    isEbfOffer?: boolean
  ) => {
    const { flightsSelectedJourneyReviewStore, listingsStore } = this.props;

    this.setState(
      {
        availableFaresInformation: availableFaresInformation,
        isEbfOffer
      },
      () => {
        flightsSelectedJourneyReviewStore.detailsVisible = true;
        listingsStore.selectedOfferIndex = selectedOfferIndex;
      }
    );
  };

  public openDetails = (
    selectedOfferIndex: number,
    availableFaresInformation:
      | FlightsStandardOffer.FlightsJourneyAvailableFaresInformation
      | FlightsBargainFareOffer.FlightsJourneyAvailableFaresInformation,
    isEbfOffer?: boolean
  ) => {
    const { flightsSelectedJourneyReviewStore } = this.props;

    if (flightsSelectedJourneyReviewStore.detailsVisible) {
      this.closeSheet();
      setTimeout(this.setDetailsOpenState.bind(this, selectedOfferIndex, availableFaresInformation, isEbfOffer), 0);
    } else {
      this.setDetailsOpenState(selectedOfferIndex, availableFaresInformation, isEbfOffer);
    }
  };

  public closeSheet = (event?: React.KeyboardEvent<HTMLButtonElement>) => {
    const { flightsSelectedJourneyReviewStore, listingsStore } = this.props;

    flightsSelectedJourneyReviewStore.detailsVisible = false;
    listingsStore.selectedOfferIndex = -1;

    if (isKeyboardEvent(event)) {
      focusElement(this.selectedOfferRef);
    }
  };

  public handleEbfOfferSelect = (
    selectOfferAction?: ActionType,
    isSmallViewport?: boolean,
    availableFaresInformationFromModal?:
      | FlightsStandardOffer.FlightsJourneyAvailableFaresInformation
      | FlightsBargainFareOffer.FlightsJourneyAvailableFaresInformation
  ) => {
    const { analytics } = this.props;
    let { availableFaresInformation } = this.state;
    availableFaresInformation =
      availableFaresInformation ||
      (availableFaresInformationFromModal as FlightsBargainFareOffer.FlightsJourneyAvailableFaresInformation);

    const applySelection = (availableFaresInformation as FlightsBargainFareOffer.FlightsJourneyAvailableFaresInformation)
      .applySelection;
    trackAnalyticsList(applySelection.analyticsList, analytics);
    const url = applySelection.relativeURI.relativePath;
    openOffer(url, isSmallViewport);
  };

  public handleOfferSelect = (selectOfferAction: ActionType, isSmallViewport?: boolean) => {
    const { analytics, dialogsStore } = this.props;
    const url = selectOfferAction.relativeURI && selectOfferAction.relativeURI.relativePath;
    const shouldOpenForcedChoiceModal =
      dialogsStore.getForcedChoiceModalDialog() &&
      dialogsStore.forcedChoiceDisplayedCounter < dialogsStore.getForcedChoiceModalDialog().displayCounter;

    trackAnalyticsList(selectOfferAction.analyticsList, analytics);

    if (
      selectOfferAction.type === FlightsActionType.LOAD_CROSS_SELL_DIALOG ||
      selectOfferAction.type === FlightsActionType.GO_TO_DETAILS
    ) {
      if (!shouldOpenForcedChoiceModal || isSmallViewport) {
        openOffer(url, isSmallViewport);
      } else {
        const packageUrl =
          selectOfferAction.relativePackageableURI && selectOfferAction.relativePackageableURI.relativePath;
        this.updateForcedChoiceModal(url, packageUrl);
      }
    }
  };

  public render() {
    const { listingsStore, dialogsStore, analytics, flightsSelectedJourneyReviewStore } = this.props;
    const { isForcedChoiceModalVisible, availableFaresInformation, isEbfOffer } = this.state;
    const selectedOfferIndex = this.props.listingsStore.selectedOfferIndex;
    const openForcedChoiceModal =
      dialogsStore.getForcedChoiceModalDialog() &&
      dialogsStore.forcedChoiceDisplayedCounter < dialogsStore.getForcedChoiceModalDialog().displayCounter;
    const offerCount = listingsStore.listings.length;
    const hygieneAmenitiesPresentation = listingsStore.getSelectedOfferHygieneAmenitiesPresentation(selectedOfferIndex);
    const { detailsVisible } = flightsSelectedJourneyReviewStore;

    return (
      <>
        <ol id="flightModuleList" className="listings no-bullet" data-test-id="listings">
          {listingsStore.listings.map((offer: FlightsListing, offerIndex: number) => {
            const lastOfferViewedAnalytics =
              offerIndex === offerCount - 1 ? listingsStore.flightsListingsAnalytics.onViewedAnalytics : null;

            return (
              <React.Fragment key={offerIndex}>
                {renderOffer(
                  offer,
                  offerIndex,
                  this.selectedOfferRef,
                  this.updateForcedChoiceModal,
                  openForcedChoiceModal,
                  this.updateOnViewedAnalytics,
                  lastOfferViewedAnalytics,
                  analytics,
                  this.openDetails,
                  selectedOfferIndex,
                  this.handleOfferSelect,
                  this.handleEbfOfferSelect
                )}
                {renderInlineIntentMedia(offerIndex)}
                {shouldMesoInlineBannerRender(offerIndex) && (
                  <MesoInlineBannerComponent bannerIndex={offerIndex} searchResultSize={offerCount} />
                )}
              </React.Fragment>
            );
          })}
        </ol>
        {isForcedChoiceModalVisible && openForcedChoiceModal && (
          <Dialogs
            dialogType="FlightsDialogForcedChoiceLodgingCrossSell"
            handleActionSelection={this.handleForcedChoiceModalAction}
          />
        )}
        <Viewport>
          <ViewLarge>
            {availableFaresInformation && (
              <DetailsAndFaresSheet
                onSheetClose={this.closeSheet}
                openForcedChoiceModal={openForcedChoiceModal}
                handleOfferSelect={isEbfOffer ? this.handleEbfOfferSelect : this.handleOfferSelect}
                showSummaryAndDetails={true}
                isDetailsVisible={detailsVisible}
                availableFaresInformation={availableFaresInformation}
                itemIndex={0}
                hygieneAmenitiesPresentation={hygieneAmenitiesPresentation}
              />
            )}
          </ViewLarge>
        </Viewport>
      </>
    );
  }
}
